import { BALANCE } from './balance';
import type { TileValue } from './board';

export const DAMAGE_BY_TILE: Readonly<Record<number, number>> = BALANCE.versus.damageByTile;

export interface DamageResult {
  readonly baseDamage: number;
  readonly multiplier: number;
  readonly damage: number;
  readonly combo: number;
  readonly mergeCount: number;
  readonly powerTiles: readonly TileValue[];
  readonly powerActivated: boolean;
}

export function damageForTile(value: TileValue): number {
  return DAMAGE_BY_TILE[value] ?? 0;
}

/** Resolves one move's combat output. `combo` is the incoming chain value. */
export function calculateDamage(mergedValues: readonly TileValue[], combo = 0): DamageResult {
  const mergeCount = mergedValues.length;
  if (mergeCount === 0) {
    return {
      baseDamage: 0,
      multiplier: 1,
      damage: 0,
      combo: 0,
      mergeCount: 0,
      powerTiles: [],
      powerActivated: false,
    };
  }

  const baseDamage = mergedValues.reduce((sum, value) => sum + damageForTile(value), 0);
  const multiplier = 1 + BALANCE.versus.multiMergeBonusPerExtraMerge * (mergeCount - 1);
  const powerTiles = mergedValues.filter((value) => BALANCE.versus.powerTiles.includes(value));

  // A small merge preserves an existing chain because the design explicitly
  // resets only on a move with no merges. It does not start a damage chain.
  const nextCombo =
    baseDamage > 0 ? combo + 1 + powerTiles.length * BALANCE.versus.powerTileComboBonus : combo;

  return {
    baseDamage,
    multiplier,
    damage: Math.floor(baseDamage * multiplier),
    combo: nextCombo,
    mergeCount,
    powerTiles,
    powerActivated: powerTiles.length > 0,
  };
}
